import React, {useMemo} from 'react';
import ClaimDetailsDialog from './ClaimDetailsDialog';
import useClaimDetailsDialogStore from '../../../../stores/useClaimDetailsDialogStore';
import {useReferenceMarkData} from '../../../../hooks/useReferenceMarkData';
import {useReferenceMarkerActions} from './hooks/useReferenceMarkerActions';
import {useReportStore} from '../../../../features/products/useReportStore';

const CLAIM_DIALOG_FIELDS = {fields: ['references', 'claims', 'suggestions']};

/**
 * Host component that renders ClaimDetailsDialog at a stable place in the tree.
 * This avoids the dialog being unmounted when margin markers collapse/re-render.
 * Claim and references are fetched live from Y.Map so supporting evidence counts
 * update as soon as verification/discovery writes them.
 */
export default function ClaimDetailsDialogHost() {
    const isOpen = useClaimDetailsDialogStore(s => s.isOpen);
    const storedClaim = useClaimDetailsDialogStore(s => s.claim);
    const referenceMarkId = useClaimDetailsDialogStore(s => s.referenceMarkId);
    const perClaimCitations = useClaimDetailsDialogStore(s => s.perClaimCitations);
    const close = useClaimDetailsDialogStore(s => s.close);
    const reportId = useReportStore(s => s.reportId);

    const {
        references,
        evidenceScores,
        claims,
        verificationLoading,
        suggestedReferences,
        contradictions,
        extrinsicNotes,
        suggestionsLoading,
        proofInsightsLoading,
    } = useReferenceMarkData(referenceMarkId, CLAIM_DIALOG_FIELDS);

    const claim = useMemo(() => {
        if (!storedClaim) return null;
        const liveClaim = claims?.find((c) => c.id === storedClaim.id);
        return liveClaim ?? storedClaim;
    }, [storedClaim, claims]);

    const {handleAddSuggestedReference, handleDeleteReference} = useReferenceMarkerActions({
        markerUid: referenceMarkId,
        fullContent: '',
        references,
        reportId,
    });

    return (
        <ClaimDetailsDialog
            open={isOpen}
            onClose={close}
            claim={claim}
            references={references}
            evidenceScores={evidenceScores}
            verificationLoading={verificationLoading}
            suggestedReferences={suggestedReferences}
            contradictions={contradictions}
            extrinsicNotes={extrinsicNotes}
            insightsLoading={proofInsightsLoading || suggestionsLoading}
            perClaimCitations={perClaimCitations}
            onAddEvidence={referenceMarkId ? handleAddSuggestedReference : undefined}
            onRemoveEvidence={referenceMarkId ? handleDeleteReference : undefined}
        />
    );
}
